import {
  number
} from "yup";

/**
 * Formats a date string to a readable format.
 * @param {string} dateString - The date string to format (e.g., "2025-01-13T11:47:29.000000Z").
 * @param {Object} options - Optional formatting options for Intl.DateTimeFormat.
 * @returns {string} - The formatted date string.
 @param {int} number

 */
export function formatDate(dateString, options = {}) {
  if (!dateString) {
    return "N/A";
  }

  const date = new Date(dateString);
  
  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }

  // Default formatting options
  const defaultOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
  };

  // Merge custom options with defaults
  const formatterOptions = {
    ...defaultOptions,
    ...options
  };

  const formatter = new Intl.DateTimeFormat("en-US", formatterOptions);
  return formatter.format(date);
}

export function formatNumber(number) {
  const formattedNumber = new Intl.NumberFormat('en-US').format(number)

  return formattedNumber;
}


export const getFormattedTimestamp = () => {
  const now = new Date();

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const date = String(now.getDate()).padStart(2, '0');

  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${date} ${hours}:${minutes}:${seconds}`;
};

/**
 * Gets the appropriate discount display based on offer type and available data
 * @param {Object} deal - The deal object
 * @returns {Object} - Object containing display text and type
 */
export const getDiscountDisplay = (deal) => {
  if (!deal) return { text: 'N/A', type: 'none' };

  // First check if bundle data exists (regardless of offer_type)
  if (deal.bundle && deal.bundle.length > 0) {
    const bundle = deal.bundle[0];
    if (bundle.bundle_savings) {
      return {
        text: `₵${formatNumber(bundle.bundle_savings)} OFF`,
        type: 'bundle',
        originalSum: bundle.original_sum,
        bundlePrice: bundle.bundle_price
      };
    }
  }

  // Then check if BOGO data exists (regardless of offer_type)
  if (deal.bogo && deal.bogo.length > 0) {
    const bogo = deal.bogo[0];
    if (bogo.bogo_free_quantity && bogo.bogo_quantity) {
      return {
        text: `Buy ${bogo.bogo_quantity} Get ${bogo.bogo_free_quantity} Free`,
        type: 'bogo'
      };
    }
  }

  // Handle regular discount offers (old structure with discounted_price)
  if (deal.discounted_price && deal.price) {
    const discountPercent = Math.round(((deal.price - deal.discounted_price) / deal.price) * 100);
    return {
      text: `${discountPercent}% OFF`,
      type: 'discount',
      discountedPrice: deal.discounted_price,
      originalPrice: deal.price
    };
  }

  // Handle new structure with discount_percentage
  if (deal.discount_percentage) {
    return {
      text: `${deal.discount_percentage}% OFF`,
      type: 'discount',
      discountPercentage: deal.discount_percentage
    };
  }

  // Fallback for deals with no discount information
  return { text: 'N/A', type: 'none' };
};